"use client";
import { format } from "date-fns";
import { ComponentProps } from "react";
import {
  StyleProp,
  StyleSheet,
  useWindowDimensions,
  ViewStyle,
} from "react-native";

import {
  BMCalendar,
  BMDivider,
  BMPrimitives,
  BMTabs,
} from "@traveloka/district-bloom";

import BMView from "../../../../components/View/BMView";
import StickyContainer from "../../Sticky";
import CalendarPicker, { RangeCalendarProps } from "./CalendarPicker";

import { BMDropdownField } from "../../../../components/BMDropdownField";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { appendTestId } from "../../../../utils";
import { clampDate } from "../../../../utils/date";
import { useDatePairPicker } from "./useDatePairPicker";

type BMCalendarProps = ComponentProps<typeof BMCalendar>;
type CalendarBaseProps = Omit<BMCalendarProps, "date" | "onChange" | "testID">;

type Props = {
  testID?: string;
  footerComponent: React.JSX.Element;
  contentStyle?: StyleProp<ViewStyle>;
} & CalendarBaseProps &
  RangeCalendarProps;

export default function CalendarPairPicker(props: Props) {
  const { CorporateCommon: cr } = useMobileContentResource();

  const { width } = useWindowDimensions();
  const isDesktop = width >= 640;

  const {
    footerComponent,
    contentStyle,
    testID,
    minDate,
    maxDate,
    value,
    onChange,
    ...calendarProps
  } = props;
  const {
    tab,
    display,
    calendar,
    calendarRef,
    goToMonth,
    onChangeTab,
    onChangeDay,
    onChangeMonth,
  } = useDatePairPicker({
    maxDate,
    minDate,
    value,
    onChange,
  });

  const [start, end] = value;

  function renderTabDate(date: Date | undefined) {
    if (!date) return "-";
    return format(date, "dd MMM yyyy");
  }

  const rangeSelectionMode = Boolean(tab === "from" || (start && end));

  function handleMonthChange(month: string | null) {
    if (month === null) return;
    goToMonth(
      clampDate(
        new Date(calendar.displayYear, Number(month), 1),
        calendar.minDate,
        calendar.maxDate
      )
    );
  }

  function handleYearChange(year: string | null) {
    if (year === null) return;
    goToMonth(
      clampDate(
        new Date(Number(year), calendar.displayMonth, 1),
        calendar.minDate,
        calendar.maxDate
      )
    );
  }

  return (
    <StickyContainer
      headerContent={
        <>
          <BMTabs
            activeTabIndex={tab === "from" ? 0 : 1}
            items={[
              {
                key: "from",
                label: cr.from,
                subLabel: renderTabDate(start),
              },
              { key: "to", label: cr.to, subLabel: renderTabDate(end) },
            ]}
            onChange={(index) => {
              onChangeTab(index === 0 ? "from" : "to");
            }}
            testID={appendTestId(testID, "tabs")}
          />
          <BMView style={styles.dropdownRow} row spacing="sm">
            <BMView flex>
              <BMDropdownField
                value={String(calendar.displayMonth)}
                option={{ items: calendar.monthOptions }}
                onChange={handleMonthChange}
                testID={appendTestId(testID, "month-dropdown")}
                isSelectedOnTop={false}
                {...(!isDesktop
                  ? { optionsSection: { title: cr.selectMonth } }
                  : {})}
              />
            </BMView>
            <BMView flex>
              <BMDropdownField
                value={String(calendar.displayYear)}
                option={{ items: calendar.yearOptions }}
                onChange={handleYearChange}
                testID={appendTestId(testID, "year-dropdown")}
                isSelectedOnTop={false}
                {...(!isDesktop
                  ? { optionsSection: { title: cr.selectYear } }
                  : {})}
              />
            </BMView>
          </BMView>
          <BMDivider type="SUBTLE" />
        </>
      }
      footerContent={footerComponent}
      contentStyle={contentStyle}
      scrollViewContentStyle={styles.scrollContent}
    >
      <CalendarPicker
        {...calendarProps}
        value={display}
        ref={calendarRef}
        onChange={onChangeDay}
        externalState={{ tab }}
        maxDate={calendar.maxDate}
        minDate={calendar.minDate}
        onVisibleDateChange={onChangeMonth}
        testID={appendTestId(testID, "calendar")}
        rangeSelectionMode={rangeSelectionMode ? "START_DATE" : "END_DATE"}
      />
    </StickyContainer>
  );
}

const styles = StyleSheet.create({
  dropdownRow: {
    padding: BMPrimitives.BMSize.md,
    zIndex: 10,
  },
  scrollContent: {
    flexGrow: 1,
  },
});
